// components/NewTicketModal.tsx
import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { CategoryEnum, PriorityEnum } from "../../modules/shared/model";
import Button from "../../components/ui/Button";

interface NewTicketModalProps {
    show: boolean;
    onClose: () => void;
    onSubmit: (ticketData: any) => void;
    newTicket: any;
    setNewTicket: (ticket: any) => void;
}

const NewTicketModal: React.FC<NewTicketModalProps> = ({ show, onClose, onSubmit, newTicket, setNewTicket }) => {
    const { t } = useTranslation();
    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-xl w-full max-w-2xl mx-4 overflow-hidden">
                <div className="p-6 border-b flex justify-between items-center">
                    <h2 className="text-xl font-semibold">{t("newTicketModal.title")}</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6">
                    <form
                        onSubmit={e => {
                            e.preventDefault();
                            onSubmit(newTicket);
                        }}>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t("newTicketModal.subject")}</label>
                                <input
                                    type="text"
                                    value={newTicket.subject}
                                    onChange={e => setNewTicket({ ...newTicket, subject: e.target.value })}
                                    className="w-full p-2 border rounded-lg"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t("newTicketModal.description")}</label>
                                <textarea
                                    value={newTicket.description}
                                    onChange={e => setNewTicket({ ...newTicket, description: e.target.value })}
                                    className="w-full p-2 border rounded-lg"
                                    rows={4}
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t("newTicketModal.priority")}</label>
                                <select
                                    value={newTicket.priority}
                                    onChange={e =>
                                        setNewTicket({
                                            ...newTicket,
                                            priority: parseInt(e.target.value),
                                        })
                                    }
                                    className="w-full p-2 border rounded-lg">
                                    <option value="">{t("newTicketModal.selectPriority")}</option>

                                    {Object.keys(PriorityEnum)
                                        .filter(key => isNaN(Number(key)))
                                        .map(key => (
                                            <option key={key} value={PriorityEnum[key]}>
                                                {key}
                                            </option>
                                        ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t("newTicketModal.category")}</label>
                                <select
                                    value={newTicket.category}
                                    onChange={e =>
                                        setNewTicket({
                                            ...newTicket,
                                            category: parseInt(e.target.value),
                                        })
                                    }
                                    className="w-full p-2 border rounded-lg"
                                    required>
                                    <option value="">{t("newTicketModal.selectCategory")}</option>
                                    {Object.keys(CategoryEnum)
                                        .filter(key => isNaN(Number(key)))
                                        .map(key => (
                                            <option key={key} value={CategoryEnum[key]}>
                                                {key}
                                            </option>
                                        ))}
                                </select>
                            </div>
                        </div>

                        <div className="mt-6 flex justify-end gap-3">
                            <Button type="button" variant="outline" onClick={onClose}>
                                {t("newTicketModal.cancel")}
                            </Button>
                            <Button
                                type="submit"
                                disabled={!newTicket.subject || !newTicket.description || !newTicket.category}>
                                {t("newTicketModal.createTicket")}
                            </Button>
                        </div>
                    </form>
                </div>
            </motion.div>
        </div>
    );
};

export default NewTicketModal;
